import React from 'react';
import { Router } from 'react-router-dom';
import { Provider } from 'react-redux';
import { createRoot } from 'react-dom/client';
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import TagManager from 'react-gtm-module';
import { PersistGate } from 'redux-persist/integration/react';
import { Toaster } from 'sonner';

import store, { persistor } from './store/store';
import history from './utils/history/customHistory';
import { App } from './components/App';
import useToaster from './utils/hooks/useToaster';
import { getTagManagerArgs } from './utils/GTM/googleTagManagerArgs';

import './global.css';
import './vitamin-play.css';

const listenWeloopClick = () => {
  document.body.onblur = event => {
    if (event.target instanceof Window && event.target.document.activeElement === document.getElementById('weloopai')) {
      TagManager.dataLayer(getTagManagerArgs({ event: 'button_clicked', label: 'widget' }));
    }
  };
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
  queryCache: new QueryCache({
    onError: (_, { meta }) => {
      if (meta?.error && typeof meta.error === 'string') {
        useToaster().addErrorToast(meta.error);
      }
    },
  }),
});

const MainApp = () => {
  const [state, setState] = React.useState({
    action: history.action,
    location: history.location,
  });

  React.useLayoutEffect(() => history.listen(setState), [history]);

  return (
    <QueryClientProvider client={queryClient}>
      <Provider store={store}>
        <Toaster position="top-right" expand />
        <PersistGate loading="..." persistor={persistor}>
          <Router location={state.location} navigationType={state.action} navigator={history}>
            <App />
          </Router>
        </PersistGate>
      </Provider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
};

listenWeloopClick();
createRoot(document.getElementById('root')!).render(<MainApp />);
